import { escapeHtml } from '../../utils/html.js';

/**
 * DeleteConfirmModal - Composant de modale de confirmation de suppression unifié
 * 
 * Utilise l'image Attention.png pour une expérience utilisateur cohérente
 * à travers toute l'application.
 * 
 * @param {Object} options - Options de configuration
 * @param {string} options.title - Titre de la modale
 * @param {string} options.message - Message principal (peut contenir du HTML)
 * @param {string} options.itemName - Nom de l'élément à supprimer (optionnel, sera mis en gras)
 * @param {string} options.warningMessage - Message d'avertissement supplémentaire (optionnel)
 * @param {string} options.confirmText - Texte du bouton de confirmation (défaut: "Supprimer définitivement")
 * @param {string} options.cancelText - Texte du bouton d'annulation (défaut: "Annuler")
 * @param {Function} options.onConfirm - Callback appelé lors de la confirmation
 * @param {Function} options.onCancel - Callback appelé lors de l'annulation (optionnel)
 * @param {number} options.zIndex - z-index de la modale (défaut: 3000)
 * @returns {Object} Instance de la modale avec méthodes show(), hide(), destroy()
 */
export function createDeleteConfirmModal(options = {}) {
  const {
    title = 'Confirmer la suppression',
    message = 'Êtes-vous sûr de vouloir supprimer cet élément ?',
    itemName = null,
    warningMessage = 'Cette action est <strong>irréversible</strong>.',
    confirmText = 'Supprimer définitivement',
    cancelText = 'Annuler',
    onConfirm = () => {},
    onCancel = () => {},
    zIndex = 3000,
  } = options;

  // Créer le conteneur de la modale
  const modal = document.createElement('div');
  modal.className = 'modal modal-delete-confirm';
  modal.style.cssText = `
    display: none;
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    align-items: center;
    justify-content: center;
    z-index: ${zIndex};
    animation: fadeIn 0.2s ease;
  `;

  // Construire le message avec le nom de l'élément si fourni
  let fullMessage = message;
  if (itemName) {
    fullMessage = `${message} <strong class="delete-item-name">${escapeHtml(itemName)}</strong>`;
  }

  // Construire le HTML de la modale
  modal.innerHTML = `
    <div class="modal-content modal-delete-content">
      <button class="modal-close delete-modal-close" aria-label="Fermer">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <div class="modal-delete-body">
        <div class="modal-delete-mouse">
          <img src="/images/souris/ATTENTION.png" alt="Attention" class="mouse-animation" />
        </div>
        <div class="modal-delete-content-wrapper">
          <h2 class="modal-delete-title">${escapeHtml(title)}</h2>
          <p class="modal-delete-text">
            ${fullMessage}
          </p>
          ${warningMessage ? `<p class="modal-delete-warning">${warningMessage}</p>` : ''}
        </div>
      </div>
      <div class="modal-delete-footer">
        <button class="modal-delete-btn modal-delete-btn-cancel delete-modal-cancel">
          <span>${escapeHtml(cancelText)}</span>
        </button>
        <button class="modal-delete-btn modal-delete-btn-confirm delete-modal-confirm">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          <span>${escapeHtml(confirmText)}</span>
        </button>
      </div>
    </div>
  `;

  // Récupérer les éléments interactifs
  const closeBtn = modal.querySelector('.delete-modal-close');
  const cancelBtn = modal.querySelector('.delete-modal-cancel');
  const confirmBtn = modal.querySelector('.delete-modal-confirm');
  const content = modal.querySelector('.modal-content');

  // Fonction pour fermer la modale
  function close(confirmed = false) {
    modal.style.display = 'none';
    
    if (confirmed && typeof onConfirm === 'function') {
      onConfirm();
    } else if (!confirmed && typeof onCancel === 'function') {
      onCancel();
    }
  }

  // Fonction pour afficher la modale
  function show() {
    document.body.appendChild(modal);
    // Force reflow pour l'animation
    modal.offsetHeight;
    modal.style.display = 'flex';
    
    // Focus sur le bouton d'annulation pour l'accessibilité
    setTimeout(() => cancelBtn?.focus(), 100);
  }

  // Fonction pour détruire la modale
  function destroy() {
    if (modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
  }

  // Event listeners
  if (closeBtn) {
    closeBtn.addEventListener('click', () => close(false));
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', () => close(false));
  }

  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => {
      close(true);
      // Détruire la modale après un court délai pour permettre l'animation
      setTimeout(destroy, 300);
    });
  }

  // Fermer sur clic en dehors du contenu
  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      close(false);
    }
  });

  // Fermer sur Escape
  const escapeHandler = (event) => {
    if (event.key === 'Escape') {
      close(false);
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Empêcher la propagation des clics sur le contenu
  if (content) {
    content.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  // Retourner l'API publique
  return {
    show,
    hide: () => close(false),
    destroy,
    element: modal,
  };
}
